import { Appointment, EpisodeOfCare, Patient } from '@medplum/fhirtypes';
import { getExtension } from '@medplum/core';
import { getCodeableConceptCode } from '@bariendo/lib/services';
import { getMetaTag } from '@bariendo/lib/meta-tags';
import { getTrackFromEpisodeOfCare } from '@bariendo/lib/episode-of-care';
import { BARIATRIC_PROCEDURE_STAGE_EXTENSION, EMPLOYER_EXTENSION } from '@bariendo/lib/extensions';
import {
  BariatricProcedureStage,
  BariatricProcedureStageLabel,
  BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM,
  Track,
  TrackLabel,
} from '@bariendo/lib/constants';

/** Reads the patient's employer from the employer extension. Returns '' when absent. */
export function getEmployer(patient: Patient): string {
  return getExtension(patient, EMPLOYER_EXTENSION)?.valueString ?? '';
}

/**
 * Derives the bariatric-procedure-stage label (e.g. "Pre-procedure") for a patient from the
 * meta tag / extension. Shared by the patient list's "Status"/"Current state" columns
 * (see PatientListPage/hooks/usePatientList.tsx#statusColumn), the Carrum CSV export, and the
 * patient export.
 * Returns '' when the stage is unknown/absent — callers rendering a table cell should apply
 * their own placeholder fallback (e.g. '--').
 */
export function getBariatricProcedureStageLabel(patient: Patient): string {
  const bariatricProcedureMetaTag = getMetaTag(patient, BARIATRIC_PROCEDURE_STAGE_TAG_SYSTEM);
  const bariatricProcedureExtension = getExtension(patient, BARIATRIC_PROCEDURE_STAGE_EXTENSION);
  const procedureStage = bariatricProcedureMetaTag?.code ?? bariatricProcedureExtension?.valueString;

  if (!procedureStage) {
    return '';
  }

  return BariatricProcedureStageLabel[procedureStage as BariatricProcedureStage];
}

/**
 * Resolves a patient's go-to-market track label ("Self-pay" / "Carrum" / "Transcarent") from their
 * EpisodeOfCare list. The track model is append-only — switching tracks closes the old episode and
 * opens a new one — so the `active` episode wins; a patient with only closed episodes falls back to
 * the first one carrying a recognised track. Returns '' when no episode carries one.
 */
export function getTrackLabel(episodes: EpisodeOfCare[] | undefined): string {
  if (!episodes?.length) {
    return '';
  }

  const activeEpisode = episodes.find((episode) => episode.status === 'active' && getTrackFromEpisodeOfCare(episode));
  const episodeWithTrack = activeEpisode ?? episodes.find((episode) => getTrackFromEpisodeOfCare(episode));
  if (!episodeWithTrack) {
    return '';
  }

  const track = getTrackFromEpisodeOfCare(episodeWithTrack);
  return track ? TrackLabel[track] : '';
}

/**
 * Track labels that mean employer-sponsored coverage: the partner tracks and the unverified employer
 * benefit. An explicit list, not "every track but self-pay" — insurance is not employer coverage, and
 * a new track must be added here on purpose.
 *
 * Note what is deliberately absent: a patient with no track episode at all. Only Carrum patients
 * carry episodes today (self-pay is not backfilled), so an unknown track must NOT be treated as
 * employer coverage — that would exclude nearly every patient.
 */
export const EMPLOYER_COVERAGE_TRACK_LABELS: ReadonlySet<string> = new Set(
  [Track.CARRUM, Track.TRANSCARENT, Track.EMPLOYER].map((track) => TrackLabel[track])
);

/** True when a track label denotes employer-sponsored coverage (Carrum, Transcarent, …). */
export function isEmployerCoverageTrackLabel(label: string): boolean {
  return EMPLOYER_COVERAGE_TRACK_LABELS.has(label);
}

/** Maps an appointment's type code to its patient-facing procedure name. Returns '' when unknown. */
export function formatProcedureType(appointment?: Appointment): string {
  const appointmentType = appointment?.appointmentType;

  if (!appointmentType) {
    return '';
  }

  const appointmentTypeCode = getCodeableConceptCode(appointmentType) ?? '';

  const HEALTHIE_TYPE_NAME_TO_CODE: Record<string, string> = {
    'app-initial-consultation-visit': 'Initial consultation',
    'esg-procedure': 'Your Everself ESG Procedure',
    'esg-max-procedure': 'Your Everself ESG Max Procedure',
    'sis-procedure': 'Your Everself SIS Procedure',
    'tore-regular-procedure': 'Your Everself TORe Procedure',
    'tore-plus-procedure': 'Your Everself TORe+ Procedure',
    'igb-placement-procedure': 'Your Everself IGB Placement Procedure',
  };

  return HEALTHIE_TYPE_NAME_TO_CODE[appointmentTypeCode] ?? '';
}
